/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState } from "react";
import axios from "axios";
import {
  ContainerResetPasswordHeader,
  EnterPasswordButton,
  EnterPasswordContainer,
  ErrorText,
  FormContainer,
  FormResetPassword,
  PasswordButtonSave,
  PasswordInput,
  PasswordRepeatInput,
  RepeatPasswordButton,
  TitleResetPassword,
} from "./ResetPasswordStyles.styled";
import { useTogglePassword } from "./PasswordHook";
import { ResetPasswordUser } from "../../API/ResetPassword/ResetPasswordUser";
import { useParams } from "react-router-dom";
import { ResetPasswordUserToken } from "../../Types/TypesPassword";
import { LogoApp } from "../../Components/LogoApp/LogoApp";
import { useLanguageContext } from "../../Components/Languages/Langs/LanguageContext";
import {
  ContainerIncludeValidationPassword,
  IndicatorPasswordValidation,
} from "../../PrivatePages/Components/SecurityPage/SecurityPageStyles.styled";
import { toast } from "react-toastify";

export const ResetPassword = () => {
  const { t } = useLanguageContext();
  const { token } = useParams<{ token: string }>();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formErrors, setFormErrors] = useState<ResetPasswordUserToken>({});
  const [isInvalidToken, setIsInvalidToken] = useState<boolean>(false);

  const enterPassword = useTogglePassword();
  const repeatPasswordToggle = useTogglePassword();

  interface PasswordStrength {
    label: string;
    value: number;
    color: string;
  }

  const [strength, setStrength] = useState<PasswordStrength | null>(null);

  const evaluatePasswordStrength = (password: string) => {
    let score = 0;

    if (password.length >= 8) score++;
    if (password.length >= 12) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/\d/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2) {
      return {
        label: t("passwordInducator.strengthWeak"),
        value: 25,
        color: "#ff4d4d",
      }; // red
    }

    if (score <= 4) {
      return {
        label: t("passwordInducator.strengthMedium"),
        value: 50,
        color: "#ffa600",
      }; // orange
    }

    if (score === 5) {
      return {
        label: t("passwordInducator.strengthStrong"),
        value: 75,
        color: "#0099ff",
      }; // blue
    }

    return {
      label: t("passwordInducator.strengthStronger"),
      value: 100,
      color: "#00cc66",
    }; // green
  };

  const changePassword = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();
    setFormErrors({});

    try {
      if (!token) return;

      await ResetPasswordUser(token, password, confirmPassword);
      toast.success("Password successfully changed!");
    } catch (err) {
      if (axios.isAxiosError(err)) {
        // Якщо токен недійсний
        if (err.response?.status === 400 || err.response?.status === 401) {
          setIsInvalidToken(true);
          return;
        }

        const errors = err.response?.data?.errors;
        if (errors) {
          setFormErrors(errors);
        }
      }
    }
  };

  // not displaying errors from server
  return (
    <>
      <ContainerResetPasswordHeader>
        <LogoApp />
      </ContainerResetPasswordHeader>

      <FormResetPassword onSubmit={changePassword}>
        <TitleResetPassword>Change password</TitleResetPassword>
        <FormContainer>
          <EnterPasswordContainer>
            <PasswordInput
              type={enterPassword.type}
              onChange={(e) => {
                const value = e.target.value;
                setPassword(e.target.value);
                setStrength(evaluatePasswordStrength(value));
              }}
              placeholder="Entry new password"
              value={password}
            />
            <EnterPasswordButton type="button" onClick={enterPassword.toggle}>
              {enterPassword.icon}
            </EnterPasswordButton>
          </EnterPasswordContainer>
          <ErrorText>{formErrors.password || ""}</ErrorText>
          <EnterPasswordContainer>
            <PasswordRepeatInput
              type={repeatPasswordToggle.type}
              onChange={(e) => {
                const value = e.target.value;
                setConfirmPassword(value); // ✅ правильне оновлення confirmPassword
                setStrength(evaluatePasswordStrength(value)); // працює індикатор
              }}
              placeholder="Repeat new password"
              value={confirmPassword}
            />
            <RepeatPasswordButton
              type="button"
              onClick={repeatPasswordToggle.toggle}
            >
              {repeatPasswordToggle.icon}
            </RepeatPasswordButton>
          </EnterPasswordContainer>
          <ErrorText>{formErrors.confirmPassword || "⠀"}</ErrorText>
          <ContainerIncludeValidationPassword>
            <IndicatorPasswordValidation
              width={strength?.value || 0}
              color={strength?.color || "#fff"}
            ></IndicatorPasswordValidation>
            <span>{strength?.label || ""}</span>
            <span>
              {t(
                "publicTranslate.FormsAndModel.ModelSignUp.RecommendationText"
              )}
              {t(
                "publicTranslate.FormsAndModel.ModelSignUp.RecomendationTitle"
              )}
            </span>
          </ContainerIncludeValidationPassword>

          <PasswordButtonSave type="submit" value="Save" />
        </FormContainer>
      </FormResetPassword>
    </>
  );
};
